import { useTranslation } from 'react-i18next'
import { UnitLabel } from '@/shared/config/config'
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  DataBlock,
  NumericInputGroup,
  isValidPercentage
} from '@/common'
import Drawer from '@/shared/components/ui/Drawer'
import calculateProcessEmissionsFromBakeAnodeConsumption from '@/features/reports/formulas/calcualteProcessEmissionsFromBakeAnodeConsumption'
import { Artifacts } from '@/api/EmissionReportModel'
import { useEffect, useState } from 'react'
import { useCalculatorValidation } from '@/hooks/useCalculatorValidation'
import { usePermissions } from '@/shared/hooks/usePermissions'

function BakeAnodeConsumptionCalculator({
  setFormula,
  formula: { netPrebakeAnodeComsumption, sulphurContent, ashContent }
}: {
  formula: {
    netPrebakeAnodeComsumption: number
    sulphurContent: number
    ashContent: number
  }
  setFormula: (
    key: 'netPrebakeAnodeComsumption' | 'sulphurContent' | 'ashContent',
    value: number
  ) => void
}) {
  const { t } = useTranslation()

  const { useInputOutsideRangeValidation: sulphurContentValidation } = useCalculatorValidation({
    minimumValue: 1,
    maximumValue: 3,
    unit: UnitLabel.wtPerCent
  })

  const { useInputAboveMaximumValueValidation: ashContentValidation } = useCalculatorValidation({
    maximumValue: 0.74,
    unit: UnitLabel.wtPerCent
  })

  const [sulphurContentWarning, setSulphurContentWarning] = useState<string>('')
  const [ashContentWarning, setAshContentWarning] = useState<string>('')

  const onNetPrebakeAnodeComsumptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value)
    setFormula('netPrebakeAnodeComsumption', value)
  }

  const onAshContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value)
    const validationData = ashContentValidation.safeParse(value)
    if (!validationData.success && value > 0) {
      setAshContentWarning(validationData.error?.issues[0].message)
    } else {
      setAshContentWarning('')
    }
    setFormula('ashContent', value)
  }

  const onSulphurContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value)
    const validationData = sulphurContentValidation.safeParse(value)
    if (!validationData.success && value > 0) {
      setSulphurContentWarning(validationData.error?.issues[0].message)
    } else {
      setSulphurContentWarning('')
    }
    setFormula('sulphurContent', value)
  }

  return (
    <div className="space-y-2">
      <NumericInputGroup
        name="netPrebakeAnodeComsumption"
        label={t(
          `report:calculators.aluminiumPrebakeAnodeCalculatorPanel.fields.netPrebakeAnodeComsumption.label`
        )}
        value={netPrebakeAnodeComsumption?.toString() || ''}
        onChange={onNetPrebakeAnodeComsumptionChange}
        suffix={UnitLabel.tAnodePerTAl}
      />
      <div className="grid grid-cols-2 gap-4">
        <NumericInputGroup
          name="sulphurContent"
          label={t(
            `report:calculators.aluminiumPrebakeAnodeCalculatorPanel.fields.sulphurContent.label`
          )}
          value={sulphurContent?.toString() || ''}
          onChange={onSulphurContentChange}
          suffix={UnitLabel.wtPerCent}
          numericFormat={{
            decimalScale: 2,
            isAllowed: isValidPercentage
          }}
          warning={sulphurContentWarning}
        />
        <NumericInputGroup
          name="ashContent"
          label={t(
            `report:calculators.aluminiumPrebakeAnodeCalculatorPanel.fields.ashContent.label`
          )}
          value={ashContent?.toString() || ''}
          onChange={onAshContentChange}
          suffix={UnitLabel.wtPerCent}
          numericFormat={{
            decimalScale: 2,
            isAllowed: isValidPercentage
          }}
          warning={ashContentWarning}
        />
      </div>
    </div>
  )
}

export default function AluminiumPrebakeAnodeCalculatorPanel({
  isOpen,
  close,
  addEmission,
  initialValues
}: {
  isOpen: boolean
  close: () => void
  initialValues?: Artifacts
  addEmission: (emission: number, artifacts: Artifacts) => void
}) {
  const { t } = useTranslation()
  const { checkPermission } = usePermissions()
  const canEditReport = checkPermission('emissionReport', 'update')
  const [formula, setFormula] = useState({
    netPrebakeAnodeComsumption: 0,
    sulphurContent: 0,
    ashContent: 0
  })

  const finalResult =
    calculateProcessEmissionsFromBakeAnodeConsumption(
      formula.netPrebakeAnodeComsumption,
      formula.sulphurContent,
      formula.ashContent
    ) || 0

  const isValid = finalResult && finalResult > 0

  const onAddClick = () => {
    if (!isValid) {
      return
    }
    const artifacts = formula
    addEmission(finalResult, artifacts)
    close()
  }

  useEffect(() => {
    if (isOpen && initialValues) {
      setFormula(initialValues)
    }
  }, [isOpen, initialValues])

  return (
    <Drawer isOpen={isOpen} close={close}>
      <Card>
        <CardHeader>
          <CardTitle title={t('report:calculators.aluminiumPrebakeAnodeCalculatorPanel.title')} />
        </CardHeader>
        <CardBody className="space-y-4">
          <BakeAnodeConsumptionCalculator
            formula={formula}
            setFormula={(key, value) => setFormula(prev => ({ ...prev, [key]: value }))}
          />

          <DataBlock label={t('report:common:emission')} data={`${finalResult} ${UnitLabel.tCO2}`} />
        </CardBody>
        <footer className="grid grid-cols-2 gap-4 pb-4 px-4">
          <Button variant="subtle" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={!isValid || !canEditReport} onClick={onAddClick}>
            {t('common:actions.save')}
          </Button>
        </footer>
      </Card>
    </Drawer>
  )
}
